import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from './api';
import { YONETIM_METIN } from './metin/yonetim';

/**
 * Parola belirleme sayfası (`/parola-kur#t=<jeton>`).
 *
 * Oturumsuz çizilir: bağlantıyı alan kişinin henüz parolası yok. Jeton adresin `#` kısmında,
 * yani sunucuya, erişim kayıtlarına ve Referer başlığına hiç gitmiyor; yalnız bu sayfanın kodu okuyor.
 * Kuralları (uzunluk, yaygın parola, kimlik) sunucu zorluyor; burada yalnız iki alanın aynı olması
 * denetleniyor ve sunucunun reddi cümle olarak ekranda kalıyor.
 */
export const PAROLA_KUR_YOLU = '/parola-kur';

function jetonOku(): string {
  const eslesme = /(?:^#|&)t=([A-Za-z0-9_-]+)/.exec(window.location.hash);
  return eslesme?.[1] ?? '';
}

export function ParolaKurPage() {
  const metin = YONETIM_METIN.parolaKur;
  const [jeton] = useState(jetonOku);
  const [ad, setAd] = useState<string | null>(null);
  const [gecerli, setGecerli] = useState<boolean | null>(null);
  const [parola, setParola] = useState('');
  const [tekrar, setTekrar] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [yapiliyor, setYapiliyor] = useState(false);
  const [bitti, setBitti] = useState(false);

  useEffect(() => {
    if (!jeton) {
      setGecerli(false);
      return;
    }
    api.checkPasswordSetup(jeton)
      .then((sonuc) => { setAd(sonuc.displayName); setGecerli(true); })
      .catch(() => setGecerli(false));
  }, [jeton]);

  const gonder = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setHata(null);
    if (parola !== tekrar) {
      setHata(metin.uyusmuyor);
      return;
    }
    setYapiliyor(true);
    try {
      await api.completePasswordSetup(jeton, parola);
      // Jeton adreste kalmasın: geri tuşuyla bu sayfaya dönülürse ölü bir bağlantı görünmesin.
      window.history.replaceState(null, '', PAROLA_KUR_YOLU);
      setBitti(true);
    } catch (caught) {
      setHata(caught instanceof ApiError ? caught.message : metin.hata);
    } finally {
      setYapiliyor(false);
    }
  };

  return (
    <main className="login-page" data-testid="parola-kur">
      <section className="login" aria-labelledby="parola-kur-baslik">
        <span className="eyebrow">{metin.ustBaslik}</span>
        <h2 id="parola-kur-baslik">{metin.baslik}</h2>
        {gecerli === null ? <p className="muted">{metin.kontrol}</p> : null}
        {gecerli === false ? <p className="error" role="alert" data-testid="parola-kur-gecersiz">{metin.gecersiz}</p> : null}
        {gecerli && bitti ? (
          <>
            <p role="status" data-testid="parola-kur-tamam">{metin.tamam}</p>
            <a className="primary full" href="/" data-testid="parola-kur-giris">{metin.girisYap}</a>
          </>
        ) : null}
        {gecerli && !bitti ? (
          <form onSubmit={gonder} aria-busy={yapiliyor}>
            <p className="muted">{metin.selam(ad ?? '')}</p>
            <label>
              {metin.parola}
              <input type="password" autoComplete="new-password" required minLength={12} maxLength={200} data-testid="parola-kur-parola" value={parola} onChange={(event) => setParola(event.target.value)} />
            </label>
            <label>
              {metin.tekrar}
              <input type="password" autoComplete="new-password" required minLength={12} maxLength={200} data-testid="parola-kur-tekrar" value={tekrar} onChange={(event) => setTekrar(event.target.value)} />
            </label>
            <p className="muted">{metin.kural}</p>
            {hata ? <p className="error" role="alert" data-testid="parola-kur-hata">{hata}</p> : null}
            <button className="primary full" type="submit" data-testid="parola-kur-gonder" disabled={yapiliyor}>
              {yapiliyor ? metin.kaydediliyor : metin.kaydet}
            </button>
          </form>
        ) : null}
      </section>
    </main>
  );
}
